// Plain rectangle maths. No board model, no rendering engine.

export type Vec = { x: number; y: number }
export type Rect = { x: number; y: number; w: number; h: number }

export const right = (r: Rect) => r.x + r.w
export const bottom = (r: Rect) => r.y + r.h
export const centre = (r: Rect): Vec => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 })

export function overlaps(a: Rect, b: Rect, gap = 0): boolean {
  return (
    a.x < right(b) + gap && b.x < right(a) + gap && a.y < bottom(b) + gap && b.y < bottom(a) + gap
  )
}

export function containsPoint(r: Rect, p: Vec): boolean {
  return p.x >= r.x && p.x <= right(r) && p.y >= r.y && p.y <= bottom(r)
}

export function contains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    right(inner) <= right(outer) &&
    bottom(inner) <= bottom(outer)
  )
}

export function union(rects: readonly Rect[]): Rect | null {
  if (!rects.length) return null
  const x = Math.min(...rects.map((r) => r.x))
  const y = Math.min(...rects.map((r) => r.y))
  return { x, y, w: Math.max(...rects.map(right)) - x, h: Math.max(...rects.map(bottom)) - y }
}

export function pad(r: Rect, p: number): Rect {
  return { x: r.x - p, y: r.y - p, w: r.w + 2 * p, h: r.h + 2 * p }
}

export function normalise(a: Vec, b: Vec): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  }
}

export function round(r: Rect): Rect {
  return { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h) }
}

/** Keep only the larger component of a drag, for ⇧ axis lock. */
export function lockToAxis(d: Vec): Vec {
  return Math.abs(d.x) >= Math.abs(d.y) ? { x: d.x, y: 0 } : { x: 0, y: d.y }
}
